import { commaizeNumber, decommaizeNumber } from "@toss/utils";
import { CountryCode, isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import PhoneNumber from "libphonenumber-js";
import _ from "lodash";
import moment from "moment";
import numeral from "numeral";
import qs from "qs";
import { useMemo } from "react";
import { routeList } from "src/routes/routes";

/* 
  number 를 받아서 천단위 콤마를 찍어서 string 으로 리턴
  ex) numberToStringWithComma(1000) => "1,000"
  ex) numberToStringWithComma(1000.34) => "1,000"
  ex) numberToStringWithComma(1000.34, "0,0.00") => "1,000.34"
  ex) numberToStringWithComma(1000.34, "0,0.0") => "1,000.3"
*/
function numberToStringWithComma(num?: number, format: string = "0,0"): string {
  // return (num || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return numeral(num).format(format);
}

/* 
  큰 숫자를 정밀도 손실 없이 문자열로 처리하여 천단위 콤마를 적용
  Number 변환 시 정밀도 손실을 방지하기 위해 문자열을 직접 처리
  ex) formatLargeNumberString("123456798123456790528") => "123,456,798,123,456,790,528"
  ex) formatLargeNumberString("123456.78") => "123,456.78"
  ex) formatLargeNumberString(123456) => "123,456"
  ex) formatLargeNumberString("invalid") => null
*/
function formatLargeNumberString(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // 문자열로 변환 후 숫자만 추출
  const numericValue = String(value).replace(/[^0-9.-]/g, "");

  // 유효한 숫자 문자열인지 검증
  if (!numericValue || !/^-?\d+\.?\d*$/.test(numericValue)) {
    return null;
  }

  // 문자열을 직접 천단위 콤마로 포맷팅
  const parts = numericValue.split(".");
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
}

/* 
  소수점 두자리까지 반올림
  ex) roundToTwo(45.666) => 45.67
*/
function roundToTwo(num?: number): number {
  const value = num || 0;
  const m = Number((Math.abs(value) * 100).toPrecision(15));
  const round = (Math.round(m) / 100) * Math.sign(value);
  return round;
}

/* 
  소수점 두자리까지 버림
  ex) floorToTwo(45.666) => 45.66
*/
function floorToTwo(num?: number): number {
  const value = num || 0;
  const m = Number((Math.abs(value) * 100).toPrecision(15));
  const floor = (Math.floor(m) / 100) * Math.sign(value);
  return floor;
}

/* 
  평수 계산 (m2 x 0.3025, 소수점 2자리 반올림). 천단위 콤마 처리
  ex) calculatePyeong(1000000) => "302,500.00"
  ex) calculatePyeong(1000000, "0,0") => "302,500"
 */
function calculatePyeong(num?: number, format: string = "0,0.00"): string {
  const pyeong = (num || 0) * 0.3025;
  return numeral(pyeong).format(format);
}

/* 
  평수 계산 (m2 x 0.3025, 소수점 2자리 반올림)
  ex) calculatePyeongNum(444444) => 134444.31
 */
function calculatePyeongNum(num?: number): number {
  const pyeong = (num || 0) * 0.3025;
  return roundToTwo(pyeong);
}

// 전용률 구하기 - 전용면적 / 계약면적 * 100
function calculateRateOfUse(
  leasableAreaNet: string | number | undefined,
  leasableArea: string | number | undefined,
  format: string = "0,0.00",
): string {
  const value = (Number(leasableAreaNet || 0) / Number(leasableArea || 0)) * 100;
  const floorValue = floorToTwo(value);
  const result = numeral(floorValue).format(format);
  return result;
}

// api용으로 사용합니다.
enum YmdFormat {
  YYYY = "YYYY",
  MM = "MM",
  YYYY_MM = "YYYY-MM",
  YYYY_MM_DD = "YYYY-MM-DD",
  TIME = "HH:mm:ss",
  YYYY_MM_DD_HH_MM = "YYYY-MM-DD HH:mm",
  FULL = "YYYY-MM-DD HH:mm:ss",
  WITH_TIME_ZONE = "YYYY-MM-DDTHH:mm:ss+09:00",
  WITH_TIME_ZONE_MILLIS = "YYYY-MM-DDTHH:mm:ss.SSS+09:00",
}

enum ViewYmdFormat {
  YYYY = "YYYY",
  MM = "MM",
  YYYY_MM = "YYYY.MM",
  YYYY_MM_DD = "YYYY-MM-DD",
  HH_MM = "HH:mm",
  TIME = "HH:mm:ss",
  YYYY_MM_DD_HH_MM = "YYYY-MM-DD HH:mm",
  FULL = "YYYY-MM-DD HH:mm:ss",
  YYYY_MM_DD_WEEK = "YYYY-MM-DD(ddd)",
  YYYY_MM_DD_WEEK_HH_MM = "YYYY-MM-DD(ddd) HH:mm",
  YYYY_MM_DD_WEEK_HH_MM_SS = "YYYY-MM-DD(ddd) HH:mm:ss",
}

/**
 * table header date view Rule
 * 대부분 목록에서만 사용
 * @param date
 * @returns { showDate = 현재 보여줘야하는 포멧 , fullDate = mouseHoverDate}
 */
const dateFormattingOperation = (date: string) => {
  //기본 포멧은 년월일
  let formatting = ViewYmdFormat.YYYY_MM_DD_WEEK;
  let returnValue = { showDate: "", fullDate: "" };
  if (date) {
    //오늘 날짜
    const today = moment().format(ViewYmdFormat.YYYY_MM_DD);
    //비교날짜
    const compareDate = moment(date).format(ViewYmdFormat.YYYY_MM_DD);
    //오늘과 같은가
    if (moment(compareDate).isSame(today)) {
      //같으면 시간 포멧으로
      formatting = ViewYmdFormat.TIME;
    }
    returnValue.showDate = moment(date).format(formatting);
    //마우스오버용 년월일 시분초 포멧
    returnValue.fullDate = moment(date).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS);
  }
  return returnValue;
};

// 전화번호 구성 변경 formatter
const PhoneFomatter = (number: string = "", type?: boolean) => {
  let formatNum = "";
  const num = number || "";

  if (num.length > 11) {
    if (num.indexOf("+82") === 0) {
      formatNum = num.replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3");
    }
  } else if (num.length === 11) {
    if (type) {
      formatNum = num.replace(/(\d{3})(\d{4})(\d{4})/, "$1-****-$3");
    } else {
      formatNum = num.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    }
  } else if (num.length === 8) {
    formatNum = num.replace(/(\d{4})(\d{4})/, "$1-$2");
  } else {
    if (num.indexOf("02") === 0) {
      if (type) {
        formatNum = num.replace(/(\d{2})(\d{4})(\d{4})/, "$1-****-$3");
      } else {
        formatNum = num.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
      }
    } else {
      if (type) {
        formatNum = num.replace(/(\d{3})(\d{3})(\d{4})/, "$1-***-$3");
      } else {
        formatNum = num.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      }
    }
  }
  return formatNum;
};

// 비용 단위 천 / 억 으로 변환
const getKoreanCost = (number: number) => {
  const koreanUnits = ["원", "만원", "억", "조"];
  let answer = "";
  let unit = 10000;
  let index = 0;
  let division = Math.pow(unit, index);

  while (Math.floor(number / division) > 0) {
    const mod = Math.floor((number % (division * unit)) / division);
    if (mod) {
      const modToString = mod.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      answer = `${modToString}${koreanUnits[index]} ` + answer;
    }
    // division = Math.pow(unit, ++index);
    division = Math.pow(unit, ++index);
  }
  return `${answer}`;
};

// 숫자만 입력
const onlyNumber = (str: any) => {
  return str.replace(/[^0-9.]/g, "").replace(/(\.*)\./g, "$1");
};

// 숫자 / 특수기호 입력
const onlyNumberAndSign = (str: any) => {
  return str.replace(/[^0-9\-\+]/g, "").replace(/(\.*)\./g, "$1");
};

// 세자리수 콤마 찍기
const inputPriceFormat = (str: any) => {
  const comma = (str: any) => {
    str = String(str);
    return str.replace(/(\d)(?=(?:\d{3})+(?!\d))/g, "$1,");
  };
  const uncomma = (str: any) => {
    str = String(str);
    return str.replace(/[^\d]+/g, "");
  };
  return comma(uncomma(str));
};

// 콤마 삭제
const deleteCommas = (str: any) => {
  if (str) {
    if (typeof str === "number") {
      return str;
    } else {
      if (str !== "") {
        return str.replace(/,/g, "");
      } else {
        return;
      }
    }
  } else {
    return;
  }
  // return str.replace(/,/g, "");
};

// 전화번호 검증은 phone-util.ts 에 있다. (기존 import 경로 호환을 위해 재수출)
export {
  INVALID_PHONE_NUMBER_MESSAGE,
  isValidKoreanMobile,
  isValidKoreanNonMobilePhone,
  isValidKoreanPhone,
  validateMobileNumber,
} from "./phone-util";

/**
 * 차량 번호 validation ("12가1234" || "123가1234")
 * @param carNumber 입력한 차량번호
 * @param isRequired 필수입력 여부 (필수입력 아닐 경우 값을 입력했을 때만 포맷 validation 체크)
 * @param requiredMessage 필수입력 에러 메세지 (없으면 "필수입력항목입니다")
 * @param formatMessage 차량번호 포맷 에러 메세지 (없으면 "차량 번호 형식에 맞게 입력해주세요")
 * @returns
 */
const validateCarNumber = (carNumber?: string, isRequired?: boolean, requiredMessage?: string, formatMessage?: string): boolean | string => {
  if (isRequired) {
    if (!carNumber) return requiredMessage || "필수입력항목입니다";
  }
  if (carNumber) {
    const pattern = /^\d{2,3}[가-힣]{1}\d{4}$/;
    return pattern.test(carNumber) || formatMessage || "차량 번호 형식에 맞게 입력해주세요";
  }
  return true;
};

// 모달에서 page 이동 시 scroll은 최상단으로 이동
// ref 타입을 HTMLElement 로 했는데 current를 잡지 못해 any로 놔두었습니다
// 사용법 - scroll을 상단으로 올릴 DOM element 를 useRef 로 잡아서 인자로 전달
const onMoveScrollTop = (element: any) => {
  element.current.scrollTo(0, 0);
};

// 분 -> 시간 변환
const showHourMinute = (onlyMinutes: string) => {
  let rtn = "";
  let hour = "";
  let minute: string | number = "";
  if (onlyMinutes) {
    if (Number(onlyMinutes) === -1) {
      hour = "제한없음";
    } else {
      if (Number(onlyMinutes) > 0) {
        hour = numberToStringWithComma(Number(Math.floor(Number(onlyMinutes) / 60).toFixed(0))) + "시간 ";
        minute = Number(Math.floor(Number(onlyMinutes) % 60).toFixed(0));
        if (minute > 0) {
          hour = hour + minute + "분";
        }
      }
    }

    rtn = hour;
  }

  return rtn;
};

const validateEmail = (email: string) => {
  const pattern = /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/g;
  return pattern.test(email);
};

const validateEmailText = (email?: string, requiredMessage?: string, formatMessage?: string): boolean | string => {
  if (!email) return requiredMessage || "필수 입력 항목입니다";
  const pattern = /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/g;
  return pattern.test(email) || formatMessage || "이메일 형식에 맞게 입력해주세요";
};

const findPgCode = (pgcode: string) => {
  let rtn = "";
  switch (pgcode) {
    case "creditcard":
      rtn = "신용카드";
      break;
    case "banktransfer":
      rtn = "계좌이체";
      break;
    case "cms":
      rtn = "CMS";
      break;
    case "virtualaccount":
      rtn = "현금(가상걔좌)";
      break;
  }
  return rtn;
};

// 법인 등록번호 중간에 하이픈 처리
const corpHypen = (str: string) => {
  let tmp = "";
  if (!str) return "";
  // 모든 숫자와 하이픈을 제외한 문자를 제거합니다.
  str = str?.replace(/[^0-9-]/g, "");

  // 하이픈을 제거하고 숫자만으로 이루어진 문자열을 생성합니다.
  const numberOnly = str?.replace(/-/g, "");

  if (numberOnly.length <= 6) {
    return numberOnly;
  } else if (numberOnly.length < 14) {
    tmp += numberOnly.substring(0, 6) + "-";
    tmp += numberOnly.substring(6);
    return tmp;
  }
};

const validatePopBillGovCode = (taxInvoiceNumber: string) => {
  const pattern = /^[a-zA-Z0-9]{8}[-\s]?[a-zA-Z0-9]{8}[-\s]?[a-zA-Z0-9]{8}$/;
  return pattern.test(taxInvoiceNumber);
};

// 사업자 등록번호 중간에 하이픈 처리
// const companyRegistrationHypen = (str: string) => {
//   let tmp = "";

//   str = str.replace(/[^0-9]/g, "");

//   if (str.length < 4) {
//     return str;
//   } else if (str.length < 6) {
//     tmp += str.substring(0, 3);
//     tmp += "-";
//     tmp += str.substring(3, 5);
//     return tmp;
//   } else if (str.length < 11) {
//     tmp += str.substring(0, 3);
//     tmp += "-";
//     tmp += str.substring(3, 5);
//     tmp += "-";
//     tmp += str.substring(5);
//     return tmp;
//   }
// };

//사업자/법인 등록번호 검증
export const checkValidateOrgNumber = (number: string) => {
  if (!number) return false;

  const cleanNumber = number.replace(/-/gi, "");
  const numberMap: number[] = cleanNumber.split("").map((d) => parseInt(d, 10));

  // 10자리(사업자등록번호) 또는 13자리(법인등록번호) 검증
  if (numberMap.length === 10 || numberMap.length === 13) {
    return true;
  }

  return false;
};

const companyRegistrationHypen = (str: string) => {
  if (!str) return "";

  // 숫자만 추출
  str = str.replace(/[^0-9]/g, "");
  let tmp = "";

  // 사업자등록번호 (10자리: xxx-xx-xxxxx)
  if (str.length < 4) {
    return str;
  } else if (str.length === 10) {
    tmp += str.substring(0, 3);
    tmp += "-";
    tmp += str.substring(3, 5);
    tmp += "-";
    tmp += str.substring(5);
    return tmp;
  }
  // 법인등록번호 (13자리: xxxxxx-xxxxxxx)
  else if (str.length === 13) {
    tmp += str.substring(0, 6);
    tmp += "-";
    tmp += str.substring(6);
    return tmp;
  }

  // 잘못된 자릿수인 경우 원본 반환
  return str;
};

// 법인 등록번호 중간에 하이픈 처리
const autoHypenCorp = (str: string) => {
  if (!str) return "";
  let tmp = "";

  str = str.replace(/[^0-9]/g, "");

  if (str.length < 6) {
    return str;
  } else if (str.length < 14) {
    tmp += str.substring(0, 6);
    tmp += "-";
    tmp += str.substring(6);
    return tmp;
  }
};

// 문자열에서 모든 하이픈 제거.
const removeHyphens = (str: string) => {
  if (!str) return;
  return str.replace(/-/g, "");
};

// date(날짜) 를 moment를 이용해 요일로 변경
const onChangeKoreanDays = (date?: string) => {
  if (date) {
    const day = moment(date).format("dddd");
    if (day) {
      let koreanDay = "";
      switch (day) {
        case "월요일":
          koreanDay = "월";
          break;
        case "화요일":
          koreanDay = "화";
          break;
        case "수요일":
          koreanDay = "수";
          break;
        case "목요일":
          koreanDay = "목";
          break;
        case "금요일":
          koreanDay = "금";
          break;
        case "토요일":
          koreanDay = "토";
          break;
        case "일요일":
          koreanDay = "일";
          break;
      }
      return koreanDay;
    }
  }
};

// 검색어 정규화용 (E.164 로 치환) — 유효성 검증이 아니므로 정규식을 그대로 둔다.
const changeListSearchPhoneNumber = (str: string) => {
  let rtnString = str;
  const patterns = [/^[+]82\d{10}$/, /^(01[016789]{1}|02|0[3-9]{1}[0-9]{1})[0-9]{3,4}[0-9]{4}$/, /^820\d{10}$/, /^[+]820\d{10}$/];
  //1. -제거
  rtnString = rtnString.replaceAll("-", "");
  //2. trim 처리
  rtnString = rtnString.replaceAll(" ", "");
  //3. (전화번호 형식이면)첫자리 0이면 +82로 치환
  if (patterns[1].test(rtnString)) {
    rtnString = "+82" + rtnString.replace("0", "");
  }
  if (patterns[2].test(rtnString)) {
    rtnString = rtnString.replace("820", "+82");
  }
  if (patterns[3].test(rtnString)) {
    rtnString = rtnString.replace("+820", "+82");
  }
  return rtnString;
};

// 핸드폰번호 +82 E.164 포맷으로 변경
const parsedPhoneNumber = (phone: string) => {
  let phoneNumber = "";
  if (phone) {
    if (isValidPhoneNumber(phone, "KR") === false) {
      return phone;
    }
    phoneNumber = parsePhoneNumber(phone, "KR").number;
  }
  return phoneNumber;
};

/**
 * 목록 조건검색의 검색어를 E.164(`+8210...`)로 치환. (QA-3847 / PS-1879)
 *
 * 사용자가 **휴대폰번호 옵션을 명시적으로 선택한 경우에만** 치환한다. 전체검색이나 검색 옵션
 * 선택 UI 가 없는 검색창은 치환하지 않는다. 전체검색 대상에는 닉네임 / 방문자명 / 계약명 /
 * 상품명 / 차량번호처럼 자유형식 필드가 있고, 특히 이메일에 `010xxxxxxxx@도메인` 형식이
 * 실제로 쓰이기 때문에, 무조건 치환하면 그 값들이 검색되지 않는다.
 *
 * 변환할 수 없는 부분검색("4631" 등)은 parsedPhoneNumber 가 원문을 그대로 돌려주므로
 * 백엔드 요청과 화면 입력값 모두 바뀌지 않는다.
 *
 * @param searchType 현재 선택된 검색 옵션
 * @param searchValue 입력한 검색어
 * @param phoneSearchType 휴대폰번호 옵션의 value (목록마다 다르다)
 */
const toPhoneSearchValue = (searchType: string | undefined, searchValue: string | undefined, phoneSearchType: string) =>
  searchType === phoneSearchType && searchValue ? parsedPhoneNumber(searchValue) : searchValue;

// E.164 포맷 -> 일반 핸드폰 포맷으로 변경 ex)010-1111-2222
const formatPhoneNumber = (phone: string) => {
  let phoneNumber = "";
  if (phone) {
    if (isValidPhoneNumber(phone, "KR") === false) {
      return phone;
    }
    phoneNumber = parsePhoneNumber(phone, "KR").formatNational();
  }
  // console.log("phone", phone);
  return phoneNumber;
};

/**
 * min 메타데이터 기준 판정. **검증용으로 쓰지 말 것.**
 *
 * KR 에 대해 사실상 자릿수만 보므로 "010-0123-4567" 같은 미할당 번호도 true 다. (QA-3842)
 * 검증에는 phone-util.ts 의 isValidKoreanMobile / isValidKoreanPhone 을 쓴다.
 * 이 함수는 BaseInput 이 "하이픈을 붙여도 되는 값인가"를 판단하는 포맷팅 전용으로만 남아 있다.
 */
export const isPhoneNumber = (phone: string, countryCode: CountryCode = "KR") => {
  return isValidPhoneNumber(phone, countryCode);
};

function getUserDevice() {
  const varUA = navigator.userAgent.toLowerCase(); // userAgent 값 얻기
  if (varUA.indexOf("android") > -1) {
    // 안드로이드
    return "android";
  } else if (varUA.indexOf("iphone") > -1 || varUA.indexOf("ipad") > -1 || varUA.indexOf("ipod") > -1 || varUA.indexOf("ios") > -1) {
    // IOS
    return "ios";
  } else {
    // IOS, 안드로이드 외 (PC)
    return "other";
  }
}

// 휴대폰번호 끝자리 4자 추출
const getFourPhoneNumber = (phone?: string) => {
  if (phone) {
    return phone.slice(-4);
  } else return "-";
};

// 숫자 3자리마다 컴마 붙이기
const changeCurrency = (value: string) => {
  let result = "";
  let count = 0;
  for (let i = value.length - 1; i >= 0; i -= 1) {
    count += 1;
    result = value[i] + result;
    if (count % 3 === 0 && i !== 0) {
      result = "," + result;
    }
  }
  return result;
};

const workSheetNoWithHyphen = (workOrderSheetNo: string) => {
  let sheetNo = workOrderSheetNo.slice(0, -2);
  sheetNo = [sheetNo.substring(0, 8), "-", sheetNo.substring(8)].join("");
  return sheetNo;
};

const isPublicPage = (pathname: string) => {
  const publicURL = "/mng/ctrl/public";
  return pathname.includes(publicURL) || pathname === "/partner";
};
const isMobilePath = (pathname: string) => {
  return pathname.includes("/mobile/");
};

export const isDevEnv = () => {
  return process.env.REACT_APP_ENVIRONMENT === "dev";
};

// pagePath return
export const ctrlRoomPath = (path: string, id?: string) => {
  const partnerId = localStorage.getItem("partnerId");
  const returnPath = `${path.replace(":partnerId", String(id ? id : partnerId))}`;
  return returnPath;
};

const columnHeader: any = [
  {
    Header: "일시",
    accessor: "datetime",
    // sticky: "left",
    width: 220,
    Cell: ({ value }: any) => {},
  },
];

// 엑셀 관련 함수들은 excelDownload.ts로 이동
// backward compatibility를 위해 re-export
export { downloadExcel, getColumnHeaderText, mapDataToExcelByColumns, filterColumnsForExcel } from "./excelDownload";

export const downloadExcelV2 = async <
  A extends string,
  T extends Record<A, any>, // T는 A의 키를 가지는 객체여야 함
>({
  data,
  fileName,
  columns,
}: {
  data: T[];
  fileName: string;
  columns: {
    Header: any;
    accessor: A; // accessor는 A의 값 중 하나여야 함
    // [key: string]: any;
  }[];
}) => {
  const XLSX = await import("xlsx");

  // 헤더 추출 (Header가 함수인 경우 처리)
  const header = columns.map((col) => (typeof col.Header === "function" ? "연결된 워크오더" : col.Header));

  // accessor 배열을 Set으로 변환하여 빠른 존재 여부 체크
  const accessorsSet = new Set(columns.map((col) => col.accessor));

  // 데이터 변환 및 accessor 체크
  const formattedData = data.map((item) => {
    // item의 키를 Set으로 변환
    const itemKeysSet = new Set(Object.keys(item));

    // 누락된 accessor를 체크
    const missingAccessors = [...accessorsSet].filter((accessor) => !itemKeysSet.has(accessor as string));

    if (missingAccessors.length > 0) {
      console.error(`Missing accessors: ${missingAccessors.join(", ")}`);
      // throw new Error(`Missing accessors: ${missingAccessors.join(", ")}`);
    }

    return columns.reduce((acc, col) => {
      const headerKey = typeof col.Header === "function" ? "연결된 워크오더" : col.Header;
      acc[headerKey] = item[col.accessor];
      return acc;
    }, {} as Record<string, any>);
  });

  console.log("formattedData :>> ", formattedData);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([]);

  XLSX.utils.sheet_add_aoa(ws, [header], { origin: "A1" });
  XLSX.utils.sheet_add_json(ws, formattedData, { origin: "A2", skipHeader: true });
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${fileName}.xlsx`);

  return true;
};

export const createExcelFile = async <A extends string, T extends Record<A, any>>({
  data,
  fileName,
  columns,
}: {
  data: T[];
  fileName: string;
  columns: {
    Header: any;
    accessor: A;
  }[];
}): Promise<File> => {
  const XLSX = await import("xlsx");
  // 헤더 추출 (Header가 함수인 경우 처리)
  const header = columns.map((col) => (typeof col.Header === "function" ? "연결된 워크오더" : col.Header));

  // 데이터 변환
  const formattedData = data.map((item) => {
    return columns.reduce((acc, col) => {
      const headerKey = typeof col.Header === "function" ? "연결된 워크오더" : col.Header;
      acc[headerKey] = item[col.accessor];
      return acc;
    }, {} as Record<string, any>);
  });

  // 워크북 생성
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([]);

  // 데이터 추가
  XLSX.utils.sheet_add_aoa(ws, [header], { origin: "A1" });
  XLSX.utils.sheet_add_json(ws, formattedData, { origin: "A2", skipHeader: true });
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  // 워크북을 바이너리 데이터로 변환
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

  // File 객체 생성 및 반환
  return new File([excelBuffer], `${fileName}.xlsx`, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// locatoin parsing 하기
const qsParse = (locationSearch: string) => {
  return qs.parse(locationSearch, { ignoreQueryPrefix: true, allowDots: true, decoder: (value) => decodeURIComponent(value) });
};

export const calculateDate = (startDate: string, endDate: string, productType?: string): string => {
  if (!startDate || !endDate) return "";

  const totalDuration = moment.duration(moment(endDate).diff(moment(startDate)));

  // FULL_COURT, OPEN_COURT는 년/개월/일 단위로 표기
  if (productType === undefined || productType === "FULL_COURT" || productType === "OPEN_COURT") {
    const startDay = moment(startDate).format(ViewYmdFormat.YYYY_MM_DD);
    const endDay = moment(endDate).format(ViewYmdFormat.YYYY_MM_DD);
    const dayDuration = moment.duration(moment(endDay).diff(moment(startDay)));
    const result: string[] = [];
    if (dayDuration.years() > 0) result.push(`${dayDuration.years()}년`);
    if (dayDuration.months() > 0) result.push(`${dayDuration.months()}개월`);
    if (dayDuration.days() > 0) result.push(`${dayDuration.days()}일`);
    return result.join(" ") || "0일";
  }

  // TIME_COURT는 24시간 이상이면 일/시간/분, 아니면 시간/분
  if (productType === "TIME_COURT") {
    const days = Math.floor(totalDuration.asDays());
    const hours = totalDuration.hours();
    const minutes = totalDuration.minutes();
    const result: string[] = [];
    if (days > 0) result.push(`${days}일`);
    if (hours > 0) result.push(`${hours}시간`);
    if (minutes > 0) result.push(`${minutes}분`);
    return result.join(" ") || "0분";
  }

  // 기타 타입은 시간/분만 표기
  const hours = totalDuration.hours();
  const minutes = totalDuration.minutes();
  const result: string[] = [];
  if (hours > 0) result.push(`${hours}시간`);
  if (minutes > 0) result.push(`${minutes}분`);
  return result.join(" ") || "0분";
};

// 이전 코드 주석처리 > 추후 이슈 없으면 삭제예정 (harry)
// export const calculateDate = (start: string, end: string, serviceType?: string) => {
//   if (serviceType === "TIME_COURT") {
//     console.log("hi");
//   }
//   let diffTime = {
//     day: 0,
//     year: 0,
//     month: 0,
//     hour: 0,
//     minute: 0,
//   };
//   let returnText = "";
//   if (start && end) {
//     if (serviceType === undefined || serviceType === "FULL_COURT" || serviceType === "OPEN_COURT") {
//       let startTime = moment(start).format(ViewYmdFormat.YYYY_MM_DD);
//       let endTime = moment(end).format(ViewYmdFormat.YYYY_MM_DD);
//       diffTime = {
//         day: moment.duration(moment(endTime).diff(moment(startTime))).days(),
//         year: moment.duration(moment(endTime).diff(moment(startTime))).years(),
//         month: moment.duration(moment(endTime).diff(moment(startTime))).months(),
//         hour: 0,
//         minute: 0,
//       };
//     } else {
//       diffTime = {
//         day: 0,
//         year: 0,
//         month: 0,
//         hour: moment.duration(moment(end).diff(moment(start))).hours(),
//         minute: moment.duration(moment(end).diff(moment(start))).minutes(),
//       };
//     }
//   }
//   // console.log("diffTime", diffTime);
//   if (serviceType === undefined || serviceType === "FULL_COURT" || serviceType === "OPEN_COURT") {
//     if (diffTime.year !== 0) {
//       returnText = diffTime.year + "년";
//     }

//     if (diffTime.month !== 0) {
//       returnText = returnText !== "" ? returnText + " " + diffTime.month + "개월" : +diffTime.month + "개월";
//     }

//     if (diffTime.day !== 0) {
//       returnText = returnText !== "" ? returnText + " " + diffTime.day + "일" : +diffTime.day + "일";
//     }
//   } else {
//     if (diffTime.hour !== 0) {
//       returnText = returnText + diffTime.hour + "시간";
//     }

//     if (diffTime.minute !== 0) {
//       returnText = returnText !== "" ? returnText + " " + diffTime.minute + "분" : +diffTime.minute + "분";
//     }
//   }

//   return returnText;
// };

// 타입가드와 타입축소용 함수

const isString = (value: any): value is string => typeof value === "string";

/** number | string | "100,000" 형식을 인자로 받아서 "100,000" | "" 형식으로 리턴 */
const commaizePrice = (value: string | number | null | undefined) => {
  if (value === "" || value === "-1" || value === -1 || value === undefined || value === null) {
    return "";
  }

  return commaizeNumber(value.toString().replace(/[^0-9.]/g, ""));
};

const typeToString = (value: string | undefined | null | number) => {
  if (value === "" || value === "-1" || value === -1 || value === null || value === undefined) {
    return "";
  }

  return value.toString();
};

/** 분 number 입력시 "x시간" or "x시간x분" 으로 리턴해주는 함수 */
const makeTimeString = (value: number) => {
  const hour = Math.floor(value / 60);
  const minute = Math.floor(value % 60);

  if (value < 60) {
    return `${value}분`;
  }

  if (minute === 0) {
    return `${hour}시간`;
  }

  return `${hour}시간 ${minute}분`;
};

/** 객체 배열을 순회하면서 특정키의 값이 중복되는 경우 true 리턴  */
function hasDuplicateValue(arr: any[], key: string): boolean {
  const priorities = new Set();

  for (let i = 0; i < arr.length; i++) {
    const priority = arr[i][key];

    if (priorities.has(priority)) {
      return true;
    }

    priorities.add(priority);
  }

  return false;
}

function generateNumberArrayFromAtoB(A: number, B: number) {
  // A부터 B까지의 숫자로 이루어진 배열 생성
  const numberArray = Array.from({ length: B - A + 1 }, (_, index) => A + index);

  return numberArray;
}

// 소수점 반올림. 1자리까지 표기
export const roundFloat = (float?: number, digit: number = 1) => {
  const toFixedFloat = (float?: number, digit: number = 1) => {
    if (_.isNil(float)) {
      return;
    }
    return float.toFixed(digit);
  };
  if (_.isNil(float)) {
    return "";
  }
  return toFixedFloat(Math.round(float * Math.pow(10, digit)) / Math.pow(10, digit));
};

export const BULLET = "•";
// 원본 데이터 타입 정의
type UDDData = {
  id?: string;
  cmdType?: "C" | "U" | "D";
  [key: string]: any; // 추가적인 키를 허용
};

/**
 * 원본 데이터와 업데이트 데이터를 비교해서 cmdType 값 변경
 * 변경된 값이 없으면 배열에서 제거
 * @param originalData
 * @param updatedData
 * @returns
 *
 */
function compareCUDData(originalData: UDDData[], updatedData: UDDData[]): UDDData[] {
  // 새로운 데이터 생성
  const newData = updatedData
    .map((updatedItem) => {
      const originalItem = originalData?.find((item) => item.id && item.id === updatedItem.id);

      console.log("originalItem :>> ", originalItem);

      if (!originalItem) {
        // 원본 데이터에 없던 경우
        return { ...updatedItem, cmdType: "C" };
      } else {
        // 원본 데이터에서 수정된 경우
        const hasChanges = Object.keys(updatedItem).some((key) => key !== "id" && key !== "cmdType" && updatedItem[key] !== originalItem[key]);

        if (hasChanges) {
          return { ...updatedItem, cmdType: "U" };
        }
        // 원본 데이터와 동일한 경우
        return null; // 동일한 항목은 null로 반환
      }
    })
    .filter((item) => item !== null); // null 제거

  // 삭제된 항목 처리
  const deletedItems = originalData
    .filter((originalItem) => !updatedData.some((updatedItem) => updatedItem.id === originalItem.id))
    .map((deletedItem) => ({
      ...deletedItem,
      cmdType: "D",
    }));

  // 최종 데이터 결합
  return [...newData, ...deletedItems] as UDDData[];
}

function sortDays(daysString: string): string {
  // 요일의 순서를 정의
  const order: string[] = ["월", "화", "수", "목", "금", "토", "일"];

  // 입력된 문자열을 배열로 변환하고, 각 요일을 trim하여 공백 제거
  const daysArray: string[] = daysString.split(",").map((day) => day.trim());

  // 요일 순서에 따라 정렬
  daysArray.sort((a, b) => order.indexOf(a) - order.indexOf(b));

  // 정렬된 배열을 다시 문자열로 변환
  return daysArray.join(",");
}

const resizedImageUrl = (url: string | undefined | null) => {
  if (!url) return "";

  const ext = url.slice(url.lastIndexOf(".")); // 파일 확장자
  const resizedImage = url.slice(0, url.lastIndexOf(".")) + "_resized" + ext;

  return resizedImage;
};

// 앞 몇글자를 제외한 마스킹처리
const maskString = (text: string, length: number) => {
  if (!text || text.length <= length) return text;

  return text.slice(0, length) + "*".repeat(text.length - length);
};

// 앞뒤 제외하고 마스킹처리
const maskMiddleChars = (text: string) => {
  if (!text || text.length <= 2) return text;
  return text.charAt(0) + "*".repeat(text.length - 2) + text.charAt(text.length - 1);
};

// 이메일 앞 3글자 제외한 마스킹
const maskEmail = (email: string, visibleLength: number = 3) => {
  if (!email || !email.includes("@")) return email;
  const [id, domain] = email.split("@");
  if (id.length <= visibleLength) return email;
  return `${id.slice(0, visibleLength)}${"*".repeat(id.length - visibleLength)}@${domain}`;
};

// 오름차순 정렬하기, 정렬할 배열과 키값을 받음.
const sortByASC = (array: any, key: string) => {
  const sortedArray = [...array];
  sortedArray.sort((a, b) => {
    if (!(key in a) && !(key in b)) return 0;
    if (!(key in a)) return -1; // key가 없는 항목을 앞으로
    if (!(key in b)) return 1; // key가 없는 항목을 뒤로

    // 숫자인 경우
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return a[key] - b[key];
    }

    // 문자열인 경우
    if (typeof a[key] === "string" && typeof b[key] === "string") {
      return a[key].localeCompare(b[key]);
    }

    // 기타 타입의 경우
    return String(a[key]).localeCompare(String(b[key]));
  });

  return sortedArray;
};

// 국제 형식 휴대폰 번호를 국내형식으로 변환
// +821000000000 → 010-0000-0000
const formatKoreanPhoneNumber = (phoneNumber: string): string => {
  if (!_.isString(phoneNumber)) {
    return "";
  }

  const parsedPhoneNumber = PhoneNumber(phoneNumber, "KR");

  if (parsedPhoneNumber) {
    return parsedPhoneNumber.formatNational();
  }

  return phoneNumber;
};

// 장기예약 날짜표기형식
const formatDateTimeRange = (start: string, end: string, type: "F2" | "S2") => {
  const startDateTime = moment(start);
  const endDateTime = moment(end);

  const dateTimeFormat = "YYYY-MM-DD HH:mm";

  const startDateTimeText = startDateTime.format(dateTimeFormat);
  const endDateTimeText = endDateTime.format(dateTimeFormat);

  const durationMinutes = endDateTime.diff(startDateTime, "minutes");
  const durationDays = Math.floor(durationMinutes / (24 * 60));
  const remainingMinutes = durationMinutes % (24 * 60);
  const durationHours = Math.floor(remainingMinutes / 60);
  const finalMinutes = remainingMinutes % 60;

  let durationText = "";
  if (durationDays > 0) {
    durationText += `${durationDays}일 `;
  }
  if (durationHours > 0) {
    durationText += `${durationHours}시간 `;
  }
  if (finalMinutes > 0) {
    durationText += `${finalMinutes}분`;
  }

  if (type === "F2") {
    return `${startDateTimeText} ~ ${endDateTimeText}`;
  } else {
    return `${startDateTimeText} ~ ${endDateTimeText} \n(${durationText.trim()})`;
  }
};

// 제어실행 결과값 parse 여부 확인
export const isValidJSONString = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

type FetchType = "parallel" | "sequential" | "batched";

/**
 *
 * @param fetchFn 호출할 execute 함수
 * @param params
 * @param totalPages 총 페이지
 * @param type 호출타입 'parallel' | 'sequential' | 'batched'; 비동기, 동기, 하이브리드, 배치
 * @param options 몇개를 병렬처리 할 건지
 * @returns
 */
const fetchDataWithType = async <T>(
  fetchFn: (params: any) => Promise<any>,
  params: any,
  totalPages: number,
  type: FetchType = "parallel",
  options?: {
    batchSize?: number;
  },
): Promise<T[]> => {
  const { batchSize = 8 } = options || {};

  switch (type) {
    case "parallel":
      // 기존 병렬 처리
      const promises = Array.from({ length: totalPages }, (_, i) => {
        return fetchFn({ ...params, page: i });
      });
      const responses = await Promise.all(promises);
      return responses.flatMap((response) => response.data.data?.content || []);

    case "sequential":
      // 완전 동기 처리
      const sequentialResults = [];
      for (let i = 0; i < totalPages; i++) {
        const response = await fetchFn({ ...params, page: i });
        sequentialResults.push(...(response.data.data?.content || []));
      }
      return sequentialResults;

    case "batched":
      // 배치 단위로 처리
      const batchedResults = [];

      for (let i = 0; i < totalPages; i += batchSize) {
        const end = Math.min(i + batchSize, totalPages);
        const batchPromises = [];

        for (let j = i; j < end; j++) {
          batchPromises.push(fetchFn({ ...params, page: j }));
        }

        const batchResponses = await Promise.all(batchPromises);
        const batchData = batchResponses.flatMap((response) => response.data.data?.content || []);
        batchedResults.push(...batchData);
      }

      return batchedResults;

    default:
      throw new Error(`Unknown fetch type: ${type}`);
  }
};

export const calculateTableWidth = (columns: Array<{ width?: number }>) => {
  return columns.reduce((total, column) => total + (column.width || 0), 0);
};

export const isUrl = (value: string): boolean => {
  const pattern = /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;
  return pattern.test(value);
};

export const createExcelFileName = (prefix: string, params: any) => {
  const timestamp = moment().format("YYYYMMDDHHmm");
  if (params.start && params.end) {
    return `ctrl.room_${prefix}_${moment(params.start).format("YYYYMMDD")}~${moment(params.end).format("YYYYMMDD")}_${timestamp}`;
  }
  return `ctrl.room_${prefix}_전체_${timestamp}`;
};

// ====== 1.31 이후 삭제하기 =====
export const ediotContent =
  "# 홍수로 인한 건물 피해를 막기위해 폐쇄합니다.\n\n\n1. 태풍 경보가 발령됐습니다.\n2. 일본은 이미 홍수로 피해를 봤습니다\n3. 우리 건물은 소중합니다.\n\n\n> 우리가 힘써야 합니다.\n\n\n***\n\n<br>\n* 어떻게 힘쓸지 알아봅시다.\n\n\\> \n\n<br>\n**극복합시다.**";
// ============================
export {
  resizedImageUrl,
  numberToStringWithComma,
  formatLargeNumberString,
  roundToTwo,
  floorToTwo,
  calculatePyeong,
  calculatePyeongNum,
  dateFormattingOperation,
  PhoneFomatter,
  YmdFormat,
  ViewYmdFormat,
  getKoreanCost,
  calculateRateOfUse,
  onlyNumber,
  deleteCommas,
  onlyNumberAndSign,
  inputPriceFormat,
  validateCarNumber,
  onMoveScrollTop,
  showHourMinute,
  findPgCode,
  validateEmail,
  validateEmailText,
  validatePopBillGovCode,
  companyRegistrationHypen,
  corpHypen,
  removeHyphens,
  onChangeKoreanDays,
  changeListSearchPhoneNumber,
  parsedPhoneNumber,
  toPhoneSearchValue,
  formatPhoneNumber,
  getUserDevice,
  getFourPhoneNumber,
  changeCurrency,
  workSheetNoWithHyphen,
  isPublicPage,
  autoHypenCorp,
  qsParse,
  isString,
  commaizePrice,
  makeTimeString,
  hasDuplicateValue,
  generateNumberArrayFromAtoB,
  compareCUDData,
  typeToString,
  sortDays,
  maskString,
  maskMiddleChars,
  maskEmail,
  sortByASC,
  formatKoreanPhoneNumber,
  formatDateTimeRange,
  isMobilePath,
  fetchDataWithType,
};
